import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { SupabaseService } from './supabase.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private supabase = inject(SupabaseService);
  private router = inject(Router);
  private logger = inject(LoggerService);

  // User state
  private _user = signal<any>(null);
  private _authReady = signal<boolean>(false);
  private _lastUserId: string | null = null;
  private authSubscription: { unsubscribe: () => void } | null = null;

  // True only while a user-initiated signOut is in flight. Distinguishes
  // intentional logouts from server-emitted SIGNED_OUT events (expired refresh
  // token, token revoked, transient refresh failure during a deploy) so the
  // latter don't forcibly kick the user from a public page.
  private userInitiatedSignOut = false;

  user = this._user.asReadonly();
  authReady = this._authReady.asReadonly();

  // Computed properties
  isAuthenticated = computed(() => !!this._user());

  constructor() {
    // Listen for auth changes
    const { data } = this.supabase.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id ?? null;

      if (event === 'SIGNED_OUT' || (newUserId && newUserId !== this._lastUserId && this._lastUserId !== null)) {
        this.clearServiceCaches();
      }

      this._lastUserId = newUserId;
      this._user.set(session?.user || null);

      if (event === 'SIGNED_OUT') {
        this._authReady.set(true);
        if (this.userInitiatedSignOut) {
          this.userInitiatedSignOut = false;
          this.router.navigate(['/login']);
        } else {
          this.logger.warn('Auth: SIGNED_OUT (non-user-initiated) — refresh token invalid');
          const url = this.router.url;
          if (url.startsWith('/u/')) {
            this.router.navigate(['/login'], { queryParams: { returnUrl: url } });
          }
        }
      } else if (session?.user) {
        this._authReady.set(true);
      } else {
        this._authReady.set(true);
      }
    });
    this.authSubscription = data?.subscription ?? null;

    // Check initial auth state
    this.checkInitialAuth();
  }

  private async checkInitialAuth() {
    try {
      const {
        data: { session },
      } = await this.supabase.getSession();
      this._user.set(session?.user ?? null);
    } catch (error) {
      this.logger.error('Error checking initial auth');
    } finally {
      this._authReady.set(true);
    }
  }

  async signIn(email: string, password: string) {
    const { data, error } = await this.supabase.signIn(email, password);
    if (error) throw error;
    return data;
  }

  async signUp(
    email: string,
    password: string,
    userData?: { given_name?: string; family_name?: string },
  ) {
    const { data, error } = await this.supabase.client.auth.signUp({
      email,
      password,
      options: {
        data: userData || {},
      },
    });
    if (error) throw error;
    return data;
  }

  async signOut() {
    this.userInitiatedSignOut = true;
    this.clearServiceCaches();
    const { error } = await this.supabase.signOut();
    if (error) {
      this.userInitiatedSignOut = false;
      throw error;
    }
  }

  private clearServiceCaches(): void {
    window.dispatchEvent(new Event('auth:clear-caches'));
  }

  // Method for guards/resolvers to wait for auth to be ready
  async waitForAuthReady(): Promise<void> {
    const MAX_WAIT_MS = 10_000; // 10 seconds
    const POLL_INTERVAL_MS = 50;

    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkReady = () => {
        if (this._authReady()) {
          resolve();
        } else if (Date.now() - startTime > MAX_WAIT_MS) {
          // Auth never became ready — resolve anyway to avoid blank screen,
          // the guard will redirect unauthenticated users to login
          this._authReady.set(true);
          resolve();
        } else {
          setTimeout(checkReady, POLL_INTERVAL_MS);
        }
      };
      checkReady();
    });
  }
}
